import { apiFetch } from './api';

const ADMIN_ACCESS_VERSION = 'system-or-configured-admin-v2';

// O token vive num cookie httpOnly (setado por picking-extras/login.php). O JS
// NAO tem acesso a ele; o Apache (prod) / setupProxy (dev) injeta o header
// Authorization a partir do cookie. Aqui guardamos so uma flag nao-sensivel.
const AUTHED_KEY = 'authed';

export const isAuthed = () => {
  try {
    return localStorage.getItem(AUTHED_KEY) === '1';
  } catch {
    return false;
  }
};

// Autentica no endpoint que grava o cookie httpOnly. Nao retorna/guarda token.
export async function loginOperator(username, password) {
  const res = await fetch('/picking-extras/login.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    throw new Error('Authentication failed. Please check your credentials.');
  }
  try {
    localStorage.setItem(AUTHED_KEY, '1');
    localStorage.setItem('operator', username);
    localStorage.removeItem('token'); // limpa token legado (pre-cookie) do navegador
  } catch {
    // ignore
  }
  return true;
}

export async function getToken() {
  // Compat: nao ha token no JS. Resolve se ha sessao; senao forca o login.
  if (isAuthed()) return 'cookie';
  throw new Error('No auth session found. Please login.');
}

export function getAuthHeaders(includeContentType = false) {
  // Sem Authorization aqui: o cookie httpOnly + injecao no proxy cuidam disso.
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

export function logout() {
  // Limpa o cookie httpOnly no servidor.
  try {
    fetch('/picking-extras/logout.php', { method: 'POST', credentials: 'same-origin' }).catch(() => {});
  } catch {
    // ignore
  }
  localStorage.removeItem(AUTHED_KEY);
  localStorage.removeItem('token'); // legado
  localStorage.removeItem('operator');
  localStorage.removeItem('roleName');
  localStorage.removeItem('roleNameKey');
  localStorage.removeItem('roleId');
  localStorage.removeItem('roleCheck');
  try {
    sessionStorage.removeItem('adminPanelAccessUser');
    sessionStorage.removeItem('adminPanelAccessVersion');
  } catch {
    // ignore
  }
  // Notifica App.js para recalcular autenticação e redirecionar para login
  window.dispatchEvent(new Event('auth-changed'));
}

export const getAdminPanelAccessUser = () => {
  try {
    return sessionStorage.getItem('adminPanelAccessUser') || '';
  } catch {
    return '';
  }
};

export const hasVerifiedAdminPanelAccess = () => {
  const currentUser = (localStorage.getItem('operator') || localStorage.getItem('username') || '').trim().toLowerCase();
  const verifiedUser = getAdminPanelAccessUser().trim().toLowerCase();
  let verifiedVersion = '';
  try {
    verifiedVersion = sessionStorage.getItem('adminPanelAccessVersion') || '';
  } catch {
    verifiedVersion = '';
  }
  return Boolean(
    currentUser &&
    verifiedUser &&
    currentUser === verifiedUser &&
    verifiedVersion === ADMIN_ACCESS_VERSION
  );
};

const isAdministratorRoleInfo = ({ roleName, roleId }) => {
  if (roleId && Number(roleId) === 1) return true;
  return String(roleName || '').trim().toLowerCase() === 'administrators';
};

const normalizeAdminUserName = (value = '') =>
  String(value).trim().replace(/\s+/g, ' ').toLowerCase();

const configuredAdminUsers = () => {
  const configured = (process.env.REACT_APP_ADMIN_USERS || '').trim();
  const source = configured || 'Augusto Cesar';
  return source
    .split(',')
    .map(normalizeAdminUserName)
    .filter(Boolean);
};

const isConfiguredAdminUser = (username = '') =>
  configuredAdminUsers().includes(normalizeAdminUserName(username));

const allowVerifiedAdminAccess = (username) => {
  try {
    sessionStorage.setItem('adminPanelAccessUser', username);
    sessionStorage.setItem('adminPanelAccessVersion', ADMIN_ACCESS_VERSION);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event('admin-access-verified'));
  return true;
};

/**
 * Concede acesso de admin de forma SINCRONA e DURAVEL para usuarios configurados,
 * sem depender de /rest/V1/users/* (que retorna 404 nesta instalacao).
 * Persiste no localStorage para sobreviver a reaberturas de aba. Retorna true se concedeu.
 */
export const grantConfiguredAdminAccessIfMatch = () => {
  const username = (localStorage.getItem('operator') || localStorage.getItem('username') || '').trim();
  if (!username || !isConfiguredAdminUser(username)) return false;
  try {
    localStorage.setItem('roleName', 'Administrators');
    localStorage.setItem('roleNameKey', 'administrators');
    localStorage.setItem('roleCheck', 'ok');
  } catch {
    // ignore
  }
  allowVerifiedAdminAccess(username);
  return true;
};

export const verifyAdminAccessWithToken = async (username) => {
  const safeUsername = String(username || '').trim();
  if (!safeUsername) return false;

  // Sem Bearer aqui: o cookie httpOnly + injecao no proxy autenticam as chamadas.
  const tempHeaders = { Accept: 'application/json' };

  let userIdentity = { userId: '', username: safeUsername };
  try {
    const meRes = await apiFetch('/rest/V1/users/me', { headers: tempHeaders });
    if (meRes.ok) {
      const meData = await meRes.json();
      const roleInfo = extractRoleInfo(meData);
      userIdentity = extractUserIdentity(meData);
      if (isAdministratorRoleInfo(roleInfo)) {
        persistRoleInfo(roleInfo);
        return allowVerifiedAdminAccess(safeUsername);
      }
    }
  } catch (err) {
    console.debug('Admin verification users/me failed:', err);
  }

  const roleInfo = await fetchRoleFromAdminUsers(userIdentity, tempHeaders);
  if (isAdministratorRoleInfo(roleInfo)) {
    persistRoleInfo(roleInfo);
    return allowVerifiedAdminAccess(safeUsername);
  }

  const systemPermissionUrls = [
    '/rest/V1/users?searchCriteria[pageSize]=1',
  ];
  for (const url of systemPermissionUrls) {
    try {
      const res = await apiFetch(url, { headers: tempHeaders });
      if (res.ok) {
        return allowVerifiedAdminAccess(safeUsername);
      }
    } catch (err) {
      console.debug('System permission check failed:', url, err);
    }
  }

  if (isConfiguredAdminUser(safeUsername)) {
    return allowVerifiedAdminAccess(safeUsername);
  }

  return false;
};

export async function verifyAdminPanelAccess(password) {
  const username = (localStorage.getItem('operator') || localStorage.getItem('username') || '').trim();
  if (!username || !password) {
    throw new Error('Please enter your username password.');
  }

  // Re-autentica via login.php (revalida a senha e renova o cookie httpOnly).
  const loginRes = await fetch('/picking-extras/login.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ username, password }),
  });
  if (!loginRes.ok) {
    throw new Error('Authentication failed. Please check your password.');
  }

  const verified = await verifyAdminAccessWithToken(username);
  if (verified) return true;

  throw new Error('Password accepted, but System admin permission was not confirmed.');
}

const extractRoleInfo = (data) => {
  if (!data || typeof data !== 'object') return { roleName: '', roleId: '' };
  let roleName = '';
  let roleId = '';

  if (Array.isArray(data.roles) && data.roles.length) {
    const candidate = data.roles.find((role) => role && (role.role_name || role.role_id !== undefined));
    if (candidate) {
      if (candidate.role_name) roleName = String(candidate.role_name);
      if (candidate.role_id !== undefined) roleId = String(candidate.role_id);
    }
  }

  if (!roleName && data.role && data.role.role_name) {
    roleName = String(data.role.role_name);
  }
  if (!roleId && data.role && data.role.role_id !== undefined) {
    roleId = String(data.role.role_id);
  }

  if (!roleName && data.role_name) {
    roleName = String(data.role_name);
  }
  if (!roleId && data.role_id !== undefined) {
    roleId = String(data.role_id);
  }
  if (!roleName && data.roleName) {
    roleName = String(data.roleName);
  }
  if (!roleId && data.roleId !== undefined) {
    roleId = String(data.roleId);
  }

  const extensionAttributes = data.extension_attributes || data.extensionAttributes;
  if (extensionAttributes && typeof extensionAttributes === 'object') {
    if (!roleName && extensionAttributes.role_name) {
      roleName = String(extensionAttributes.role_name);
    }
    if (!roleId && extensionAttributes.role_id !== undefined) {
      roleId = String(extensionAttributes.role_id);
    }
  }

  return { roleName, roleId };
};

const extractUserIdentity = (data) => {
  if (!data || typeof data !== 'object') return { userId: '', username: '' };
  const userId = data.id ?? data.user_id ?? data.userId ?? '';
  const username =
    data.username ??
    data.user_name ??
    data.userName ??
    localStorage.getItem('operator') ??
    localStorage.getItem('username') ??
    '';
  return {
    userId: userId === undefined || userId === null ? '' : String(userId),
    username: username === undefined || username === null ? '' : String(username).trim(),
  };
};

const persistRoleInfo = ({ roleName, roleId }) => {
  if (roleName) {
    localStorage.setItem('roleName', roleName);
    localStorage.setItem('roleNameKey', roleName.toLowerCase());
  } else {
    localStorage.removeItem('roleName');
    localStorage.removeItem('roleNameKey');
  }
  if (roleId) {
    localStorage.setItem('roleId', roleId);
  } else {
    localStorage.removeItem('roleId');
  }
};

const fetchRoleFromAdminUsers = async ({ userId, username }, headers = getAuthHeaders()) => {
  const attempts = [];
  if (userId) {
    attempts.push(`/rest/V1/users/${encodeURIComponent(userId)}`);
  }
  if (username) {
    attempts.push(
      `/rest/V1/users?searchCriteria[filter_groups][0][filters][0][field]=username` +
      `&searchCriteria[filter_groups][0][filters][0][value]=${encodeURIComponent(username)}` +
      '&searchCriteria[filter_groups][0][filters][0][condition_type]=eq'
    );
  }

  for (const url of attempts) {
    try {
      const res = await apiFetch(url, { headers });
      if (!res.ok) {
        console.debug('Admin user role fallback unavailable:', url, res.status);
        continue;
      }
      const data = await res.json();
      const candidate = Array.isArray(data?.items) ? data.items[0] : data;
      const roleInfo = extractRoleInfo(candidate);
      if (roleInfo.roleName || roleInfo.roleId) {
        return roleInfo;
      }
    } catch (err) {
      console.debug('Admin user role fallback failed:', url, err);
    }
  }

  return { roleName: '', roleId: '' };
};

export async function fetchAndStoreUserRole() {
  try {
    const res = await apiFetch('/rest/V1/users/me', {
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const body = await res.text();
      if (res.status === 403 || res.status === 404) {
        localStorage.setItem('roleCheck', 'none');
        localStorage.removeItem('roleName');
        localStorage.setItem('roleNameKey', '');
        console.warn('Role endpoint not accessible; treating as non-admin.');
        return '';
      }
      throw new Error(body || `users/me failed (${res.status})`);
    }
    const data = await res.json();
    let { roleName, roleId } = extractRoleInfo(data);
    console.debug('users/me payload', data);

    if (!roleName && !roleId) {
      const identity = extractUserIdentity(data);
      const fallbackRole = await fetchRoleFromAdminUsers(identity);
      roleName = fallbackRole.roleName;
      roleId = fallbackRole.roleId;
    }

    persistRoleInfo({ roleName, roleId });
    localStorage.setItem('roleCheck', 'ok');
    window.dispatchEvent(new Event('user-meta-updated'));
    return roleName || roleId;
  } catch (err) {
    console.warn('Failed to determine user role:', err);
    localStorage.removeItem('roleName');
    localStorage.removeItem('roleNameKey');
    localStorage.removeItem('roleId');
    localStorage.setItem('roleCheck', 'error');
    window.dispatchEvent(new Event('user-meta-updated'));
    return '';
  }
}
